"use client";

import { useRouter } from "next/navigation";
import { authClient } from "@/lib/auth/auth-client";
import { NavbarTwoRow } from "@/components/layout/navbar-two-row";
import type { Session, User } from "@/lib/auth/auth";
import { useEffect } from "react";
import { useWalletStore } from "@/store/wallet-store";

interface NavbarTwoRowWithAuthProps {
  initialSession: {
    session: Session;
    user: User;
  } | null;
  logoHref?: string;
  showWallet?: boolean;
}

export function NavbarTwoRowWithAuth({
  initialSession,
  logoHref = "/",
  showWallet = true,
}: NavbarTwoRowWithAuthProps) {
  const router = useRouter();

  // Use reactive session hook for real-time updates
  const { data: clientSession, isPending } = authClient.useSession();

  // Get wallet balance from store
  const walletBalance = useWalletStore((state) => state.balance);
  const refreshBalance = useWalletStore((state) => state.refreshBalance);

  // Once client session is loaded, always use it
  const session = isPending ? initialSession : clientSession;

  useEffect(() => {
    // Fetch wallet balance if user is authenticated
    if (showWallet && session?.user) {
      refreshBalance();
    }
  }, [session?.user, showWallet, refreshBalance]);

  const handleSignOut = async () => {
    await authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          router.push("/");
        },
      },
    });
  };

  return (
    <NavbarTwoRow
      logoHref={logoHref}
      showWallet={showWallet && !!session?.user}
      walletBalance={walletBalance}
      walletHref="/wallet"
      showAuth
      isAuthenticated={!!session?.user}
      user={
        session?.user
          ? {
              name: session.user.name,
              email: session.user.email,
              image: session.user.image || undefined,
            }
          : undefined
      }
      onSignOut={handleSignOut}
      signInHref="/sign-in"
    />
  );
}
